// AI emits an InputFrame each tick — same shape as the human input path.
// Hierarchical FSM:
//   Idle / Approach / Attack / JumpKick / Stunned / GrabAndThrow / ThrowKOd
//   + Climb (sub-level: pull up onto deck or out of water)
//   + Showboat (only enemy is already being engaged by a teammate)

import { InputBtn, type InputFrame, emptyInput } from '../../core/InputFrame';
import { Rng } from '../../core/Rng';
import type { CharacterSnapshot, SimState, TeamId } from '../SimState';

type AIState =
  | 'Idle' | 'Approach' | 'Attack' | 'JumpKick' | 'Stunned'
  // New: dedicated grab-and-throw flow. Tries to latch both hands on the
  // opponent; once double-holding, waits briefly then throws. Gives up if it
  // can't double-hold within a timeout.
  | 'GrabAndThrow'
  // New: triggered exclusively when the opponent is KO'd. AI ONLY tries to
  // pick them up + throw them; ignores all other behaviors. Approaches to a
  // tighter-than-ideal distance to guarantee the grab lands.
  | 'ThrowKOd'
  // Sub-level only: hold Grab + pulse Jump + push forward to climb out of
  // water onto the gunwale, or pull up onto a raised deck section the target
  // is standing on.
  | 'Climb'
  // Only living enemy is already being engaged by a teammate. Spin + hop +
  // occasional shadow-punch in place until the situation changes.
  | 'Showboat';

export type AIDifficulty = 'passive' | 'easy' | 'normal' | 'hard';

// Pelvis-to-pelvis target spacing. The NPC approaches up to this range and
// then holds — if the player chooses to walk into them, that's the player's
// problem; we don't back off. Kick / sprint ranges keyed off the same.
const IDEAL_DIST = 1.2;
const APPROACH_EXIT = IDEAL_DIST * 1.08;   // stop approaching just past ideal
const APPROACH_ENTER = IDEAL_DIST * 1.35;  // start approaching when we drift out
const KICK_RANGE_MIN = IDEAL_DIST * 1.8;   // ≈ 3.24 m — commit a jumpkick from here
const KICK_RANGE_MAX = IDEAL_DIST * 3.1;   // ≈ 5.58 m — beyond this, just run in
const SPRINT_MIN_DIST = IDEAL_DIST * 1.25; // don't burn stamina when already close

// Grab-and-throw tuning (matches the user's spec).
const GRAB_TRY_TIMEOUT_SEC = 1.5;     // give up after this long without latching both hands
const GRAB_HOLD_THROW_DELAY_SEC = 1.0; // once double-holding, wait this long before releasing the throw
const GRAB_ATTEMPT_DIST = IDEAL_DIST * 1.0;   // press Grab once we're within this range
const KO_GRAB_APPROACH_DIST = IDEAL_DIST * 0.78; // closer than IDEAL so the latch is a guarantee

// Attack action mix (punch / headbutt only — grab has its own top-level
// state now). Ratios re-normalised so they still sum to 1.
const ACTION_PUNCH  = 0.65;
const ACTION_HEADBUTT = 0.35;
// Probability per-second of starting a grab-and-throw attempt from Idle when
// the opponent is standing. Rolls once per tick (scaled by dt); higher
// difficulties reach for the grab more aggressively.
const GRAB_CHANCE_PER_SEC = {
  easy: 0.10,
  normal: 0.18,
  hard: 0.30,
  passive: 0
} as const;

// Per-difficulty tempo + sprint behaviour. punchBurstSec / restAfterPunchSec /
// restAfterHeadbuttSec drive the user-requested "breathing room" combat:
// easy = long flurry then a clear pause; hard = the snappy old behaviour.
const DIFFICULTY_TUNING = {
  easy: {
    sprintChancePerSec: 0.10, sprintStaminaFloor: 0.55, jumpKickChance: 0.10,
    punchBurstSec: 1.6, restAfterPunchSec: 1.0, restAfterHeadbuttSec: 1.0,
    headbuttHoldSec: 0.08
  },
  normal: {
    sprintChancePerSec: 0.35, sprintStaminaFloor: 0.40, jumpKickChance: 0.20,
    punchBurstSec: 0.6, restAfterPunchSec: 0.4, restAfterHeadbuttSec: 0.4,
    headbuttHoldSec: 0.07
  },
  hard: {
    sprintChancePerSec: 0.70, sprintStaminaFloor: 0.25, jumpKickChance: 0.30,
    punchBurstSec: 0.1, restAfterPunchSec: 0.15, restAfterHeadbuttSec: 0.15,
    headbuttHoldSec: 0.06
  },
  passive: {
    sprintChancePerSec: 0,    sprintStaminaFloor: 1.0,  jumpKickChance: 0,
    punchBurstSec: 0,         restAfterPunchSec: 0,     restAfterHeadbuttSec: 0,
    headbuttHoldSec: 0
  }
} as const;

// Sub-level navigation tuning. All section-3 logic short-circuits when
// `mapNeedsNav` is false, so non-sub maps run at the original cost.
const LOS_CHECK_INTERVAL_SEC = 1.0;
const LOS_RAY_VERT_OFFSET = 0.5;     // raise the ray off the pelvis a bit
const LOS_DODGE_HOLD_SEC = 1.0;
const LOS_DODGE_RAY_DIST = 3.0;
const LOS_DODGE_ANGLE = Math.PI / 3; // 60° off direct path
const CLIMB_HEIGHT_DELTA = 1.2;       // target this much above me => try to climb
const CLIMB_HORIZONTAL_DIST = 3.0;
const CLIMB_EXIT_DRY_TICKS = 2;       // consecutive dry observations to confirm
const SUB_NAV_MAP_ID = 'sinkingShip';

// Showboat tuning.
const SHOWBOAT_ENGAGED_RANGE = 3.0;   // teammate within this range counts as engaging
const SHOWBOAT_SPIN_RATE = 1.5;       // rad/s
const SHOWBOAT_JUMP_PERIOD_SEC = 1.2;
const SHOWBOAT_PUNCH_PERIOD_SEC = 2.5;

export interface AIRaycastFn {
  /** Cast a ray from `origin` in `dir` (unit vector) up to `maxDist` against
   *  arena geometry only. Returns the time-of-impact (0..maxDist) on hit, or
   *  null on miss. */
  (origin: { x: number; y: number; z: number }, dir: { x: number; y: number; z: number }, maxDist: number): number | null;
}

export interface AIOptions {
  selfId: number;
  reactionTicks: number;
  aimNoise: number;
  difficulty: AIDifficulty;
  /** Team lookup. When provided, the AI targets the nearest living *enemy*
   *  (different team) and never picks teammates as opponents. When absent,
   *  falls back to the pre-team behaviour (nearest non-self character). */
  teamOf?: (id: number) => TeamId;
  /** Current map id. Drives whether sub-level nav (water exit, climbing,
   *  obstacle dodging) is enabled. Defaults to '' (no nav). */
  mapId?: string;
  /** Cast a ray against arena geometry. Required for sub-level nav; if
   *  omitted, LOS is assumed clear (i.e. behave like today). */
  raycastArena?: AIRaycastFn;
  /** Returns the set of enemy ids my AI teammates are currently targeting
   *  (excluding me). Used for target dedup. If omitted, falls back to
   *  nearest-enemy with no awareness of teammate intent. */
  getTeammateTargetIds?: (myId: number) => Iterable<number>;
}

export class AIController {
  private state: AIState = 'Idle';
  private stateT = 0;
  private rng = new Rng(0xa1b2c3d4);
  private heldBtns = 0;
  private heldRemaining = 0;
  private lastAimYaw = 0;
  private snapshotHistory: SimState[] = [];
  // Sprint is held in bursts: when a roll triggers, we lock sprint on for
  // 0.6–1.6 s to produce legible "running at you" behaviour rather than
  // a jittery single-tick press.
  private sprintHold = 0;
  // JumpKick sequence: phase 0 = press Jump, phase 1 = press JumpKick while
  // airborne. stateT in JumpKick state drives the transitions.
  private jumpKickPhase: 0 | 1 = 0;
  // Gate between attack decisions so we don't re-enter Attack on the same
  // frame the previous one ended.
  private attackCooldown = 0;
  // Grab-and-throw bookkeeping. `grabThrowTimer` counts down from
  // GRAB_HOLD_THROW_DELAY_SEC once we've reached double-hold; on 0 we fire
  // a single Punch edge (the throw).
  private grabThrowTimer = 0;
  // Edge-press latch for Punch: emit the Punch bit on exactly this many
  // upcoming ticks (1 = one-frame press), so Character sees a proper edge.
  private punchPressTicks = 0;
  // Gate between grab attempts (non-KO'd path) so we don't immediately
  // re-roll a grab the instant the last one gave up.
  private grabCooldown = 0;
  // Last picked enemy id. Read by other AIs (via getTeammateTargetIds) so
  // teammates avoid stacking on the same victim. null while stunned / no
  // valid enemy / we don't currently have a target picked.
  private _currentTargetId: number | null = null;
  // Sub-level navigation cache. Re-checked every LOS_CHECK_INTERVAL_SEC.
  private losCheckTimer = 0;
  private losClear = true;
  private dodgeActive = 0;        // seconds left we should walk a sidestep
  private dodgeAimYaw = 0;        // world yaw for the sidestep walk direction
  // Climb micro-state bookkeeping.
  private climbDryTicks = 0;
  // Showboat bookkeeping.
  private showboatJumpTimer = 0;
  private showboatPunchTimer = 0;
  // True only on maps that actually need geometry-aware nav. Cached from
  // opts.mapId at construction so the per-tick check is a boolean read.
  private readonly mapNeedsNav: boolean;

  constructor(private opts: AIOptions) {
    this.mapNeedsNav = (opts.mapId === SUB_NAV_MAP_ID) && typeof opts.raycastArena === 'function';
  }

  /** Currently engaged enemy id, or null while idle / stunned. Other AIs
   *  read this through ServerSim's getTeammateTargetIds callback. */
  get currentTargetId(): number | null { return this._currentTargetId; }

  observe(state: SimState): void {
    this.snapshotHistory.push(state);
    if (this.snapshotHistory.length > 16) this.snapshotHistory.shift();
  }

  produce(tick: number, dt: number): InputFrame {
    // 'passive' = sandbag mode: AI never inputs anything. Useful for testing
    // your own movement & combat without an opponent fighting back.
    if (this.opts.difficulty === 'passive') {
      this._currentTargetId = null;
      return emptyInput(tick);
    }
    const idx = Math.max(0, this.snapshotHistory.length - 1 - this.opts.reactionTicks);
    const observed = this.snapshotHistory[idx];
    const frame = emptyInput(tick);
    if (!observed) return frame;
    // Indexed scan instead of `Array.find()` — saves the per-call closure
    // allocation and lets the JIT inline the comparison cleanly.
    let me: CharacterSnapshot | undefined;
    for (let i = 0; i < observed.characters.length; i++) {
      const c = observed.characters[i]!;
      if (c.id === this.opts.selfId) { me = c; break; }
    }
    if (!me) return frame;
    // Target = nearest living enemy (team-aware, dedup-aware). Skip teammates
    // and self; skip anyone already wasted or ringedOut so we stop actively
    // chasing ghosts. KO'd-but-not-out enemies ARE valid targets (that's
    // the ThrowKOd pickup flow).
    const livingEnemies = this.collectLivingEnemies(me, observed.characters);
    const opp = this.pickTarget(me, livingEnemies);

    // Showboat check: if we have only one living enemy and a teammate is
    // already engaging it (within combat range), do an idle showboat. This
    // covers both "AI buddy is on them" and "human is finishing them" without
    // needing to inspect grab victim ids — proximity is enough.
    if (opp && livingEnemies.length === 1 && this.teammateEngaging(me, opp, observed.characters)) {
      this._currentTargetId = opp.id;
      if (this.state !== 'Showboat') {
        this.state = 'Showboat';
        this.stateT = 0;
        this.showboatJumpTimer = 0;
        this.showboatPunchTimer = 0;
      }
    } else if (this.state === 'Showboat') {
      // Conditions broke (another enemy stood, or teammate left) → resume
      // normal pursuit.
      this.state = 'Idle';
      this.stateT = 0;
    }

    if (!opp) {
      this._currentTargetId = null;
      return frame;
    }
    this._currentTargetId = opp.id;

    if (me.knockedOut || me.ringedOut || me.wasted || me.koStunRemaining > 0) {
      this.state = 'Stunned';
      this._currentTargetId = null;
      return frame;
    }
    if (me.stunRemaining > 0.05) {
      this.state = 'Stunned';
      this._currentTargetId = null;
      return frame;
    } else if (this.state === 'Stunned') {
      this.state = 'Idle';
      this.stateT = 0;
    }

    if (this.attackCooldown > 0) this.attackCooldown = Math.max(0, this.attackCooldown - dt);
    if (this.sprintHold > 0) this.sprintHold = Math.max(0, this.sprintHold - dt);
    if (this.grabCooldown > 0) this.grabCooldown = Math.max(0, this.grabCooldown - dt);
    if (this.dodgeActive > 0) this.dodgeActive = Math.max(0, this.dodgeActive - dt);

    const dx = opp.pelvisX - me.pelvisX;
    const dz = opp.pelvisZ - me.pelvisZ;
    const dy = opp.pelvisY - me.pelvisY;
    const dist = Math.hypot(dx, dz);
    const tune = DIFFICULTY_TUNING[this.opts.difficulty];

    // Showboat owns the rest of this tick — emit its inputs and bail before
    // the FSM tries to commit to combat actions.
    if (this.state === 'Showboat') {
      return this.produceShowboatFrame(frame, me, dt);
    }

    // --- Sub-level navigation pre-checks ---
    // Run before the FSM transitions so Climb can override Approach/Attack
    // gates when we need to pull up out of water or onto a ledge.
    if (this.mapNeedsNav) {
      this.maybeUpdateLOS(me, opp, dt);
      this.maybeEnterClimb(me, opp, dist, dy);
    }

    // --- FSM transitions ---
    this.stateT += dt;

    // Priority override: opponent is KO'd → the ONLY thing we do is walk up
    // to them and throw. Nothing else (punch / headbutt / jumpkick / retreat)
    // should fire while they're on the ground recovering.
    const oppKOd = opp.knockedOut || opp.koStunRemaining > 0 || opp.wasted || opp.ringedOut;
    if (oppKOd && this.state !== 'Climb') {
      if (this.state !== 'ThrowKOd') {
        this.state = 'ThrowKOd';
        this.stateT = 0;
        this.grabThrowTimer = 0;
        this.heldBtns = 0;
        this.heldRemaining = 0;
      }
    } else if (!oppKOd && this.state === 'ThrowKOd') {
      // Opp got back up mid-setup — bail back to Idle.
      this.state = 'Idle';
      this.stateT = 0;
      this.grabThrowTimer = 0;
    }

    if (this.state === 'Climb') {
      // Exit conditions: not swimming for several ticks AND we've climbed up
      // to within reach of the target's height (or the height delta closed).
      const dry = !me.isSwimming;
      this.climbDryTicks = dry ? this.climbDryTicks + 1 : 0;
      const heightCleared = dy < CLIMB_HEIGHT_DELTA * 0.5;
      if (this.climbDryTicks >= CLIMB_EXIT_DRY_TICKS && heightCleared) {
        this.state = 'Idle';
        this.stateT = 0;
      }
    } else if (this.state === 'Idle' || this.state === 'Approach') {
      const grabRollChance = GRAB_CHANCE_PER_SEC[this.opts.difficulty] * dt;
      // Gate 0: grab-and-throw, rolled occasionally from Idle when we're
      // close enough. Runs before the jumpkick/attack gates so the AI mixes
      // grabs into its close-quarters rotation.
      if (
        dist < APPROACH_ENTER &&
        this.stateT > 0.3 &&
        this.attackCooldown <= 0 &&
        this.grabCooldown <= 0 &&
        me.stamina > 0.35 &&
        this.rng.next() < grabRollChance
      ) {
        this.state = 'GrabAndThrow';
        this.stateT = 0;
        this.grabThrowTimer = 0;
      }
      // Gate 1: committed jump-kick from medium range. Rolled once per
      // ~second of idle/approach time to avoid spammy kicks on tick-rate.
      else if (
        dist >= KICK_RANGE_MIN && dist <= KICK_RANGE_MAX &&
        this.stateT > 0.4 && this.attackCooldown <= 0 &&
        me.stamina > 0.55 &&
        this.rng.next() < tune.jumpKickChance * dt * 2
      ) {
        this.state = 'JumpKick';
        this.stateT = 0;
        this.jumpKickPhase = 0;
        this.attackCooldown = 1.2;
      }
      // Gate 2: close enough to punch/headbutt.
      else if (dist < APPROACH_EXIT && this.attackCooldown <= 0) {
        this.beginAttack();
      }
      // Gate 3: out of range → approach.
      else if (this.state === 'Idle' && dist > APPROACH_ENTER) {
        this.state = 'Approach';
        this.stateT = 0;
      }
      // Gate 4: already in approach, reached ideal range → idle/hold.
      else if (this.state === 'Approach' && dist < APPROACH_EXIT) {
        this.state = 'Idle';
        this.stateT = 0;
      }
    } else if (this.state === 'Attack' && this.heldRemaining <= 0) {
      // Choose post-attack rest based on which attack was held: Punch held
      // → punch rest; JumpKick (= headbutt on the ground) → headbutt rest.
      const wasHeadbutt = (this.heldBtns & InputBtn.JumpKick) !== 0;
      this.state = 'Idle';
      this.stateT = 0;
      this.attackCooldown = wasHeadbutt ? tune.restAfterHeadbuttSec : tune.restAfterPunchSec;
    } else if (this.state === 'JumpKick' && this.stateT > 0.9) {
      this.state = 'Idle';
      this.stateT = 0;
    } else if (this.state === 'GrabAndThrow') {
      // Phase A: trying to latch. Count both hands stuck as "got them".
      if (me.doubleHolding && this.grabThrowTimer <= 0) {
        this.grabThrowTimer = GRAB_HOLD_THROW_DELAY_SEC;
      }
      // Phase B: double-holding, counting down to the throw.
      if (this.grabThrowTimer > 0) {
        this.grabThrowTimer = Math.max(0, this.grabThrowTimer - dt);
        if (this.grabThrowTimer <= 0) {
          // Fire throw (single Punch edge), release Grab, exit.
          this.punchPressTicks = 1; // one-tick Punch edge = throw
          this.state = 'Idle';
          this.stateT = 0;
          this.attackCooldown = 0.35;
        }
      } else if (this.stateT > GRAB_TRY_TIMEOUT_SEC) {
        // Give up — couldn't get both hands on in time.
        this.state = 'Idle';
        this.stateT = 0;
        this.grabCooldown = 1.2;
      }
    } else if (this.state === 'ThrowKOd') {
      // Same grab+hold+throw structure, but we STAY in this state until the
      // throw fires (or the opp un-KOs, handled above).
      if (me.doubleHolding && this.grabThrowTimer <= 0) {
        this.grabThrowTimer = GRAB_HOLD_THROW_DELAY_SEC;
      }
      if (this.grabThrowTimer > 0) {
        this.grabThrowTimer = Math.max(0, this.grabThrowTimer - dt);
        if (this.grabThrowTimer <= 0) {
          this.punchPressTicks = 1;
          // Don't exit — stay in ThrowKOd until opp is no longer KO'd.
          // (The priority override at the top of this function handles the
          // exit once they stand.) For now the throw launches them; AI
          // resets its hold timer so if they're STILL KO'd after landing
          // we can approach and throw again.
          this.grabThrowTimer = 0;
        }
      }
    }

    // --- Aim ---
    // Default: face opponent. While dodging an obstacle, face the dodge
    // direction so moveZ=-1 carries us around it; the LOS recheck will
    // restore direct facing once it clears.
    let targetAimYaw = Math.atan2(dx, dz);
    if (this.dodgeActive > 0 && this.state === 'Approach') {
      targetAimYaw = this.dodgeAimYaw;
    }
    let aim = targetAimYaw;
    aim += (this.rng.next() - 0.5) * this.opts.aimNoise;
    const dyaw = wrapAngle(aim - this.lastAimYaw);
    aim = this.lastAimYaw + dyaw * Math.min(1, dt * 8);
    this.lastAimYaw = aim;
    frame.aimYaw = aim;

    // --- Movement + buttons ---
    if (this.state === 'Climb') {
      // Hold Grab + hold Jump + push forward. The surface pull-up state
      // machine (Character.ts: requires Jump HELD continuously while
      // double-surface-holding) drives the climb itself; pulsing Jump
      // would abort the wind phase every tick. Holding it lets the cycle
      // re-arm naturally as the pull-up cooldown lapses, which is how a
      // human player chains pull-ups up a wall.
      frame.moveZ = -1;
      frame.buttons |= InputBtn.Grab | InputBtn.Jump;
    } else if (this.state === 'Approach') {
      // Forward in AI aim-frame = toward opponent (or toward dodge direction
      // if we're routing around an obstacle).
      frame.moveZ = -1;
      // Sprint decision: rolled on entry, held for a burst. Gated by
      // stamina floor and only bursts when we're well outside punch range.
      if (
        this.sprintHold <= 0 &&
        dist > SPRINT_MIN_DIST &&
        me.stamina > tune.sprintStaminaFloor &&
        this.rng.next() < tune.sprintChancePerSec * dt
      ) {
        this.sprintHold = 0.6 + this.rng.next() * 1.0;
      }
      if (this.sprintHold > 0 && me.stamina > 0.10) {
        frame.buttons |= InputBtn.Sprint;
      }
    } else if (this.state === 'Attack') {
      this.heldRemaining -= dt;
      frame.buttons |= this.heldBtns;
      // Only nudge forward if we've drifted too far; never back off — if
      // the player walks into us, that's their call.
      if (dist > IDEAL_DIST * 1.1) frame.moveZ = -0.35;
    } else if (this.state === 'JumpKick') {
      // Phase 0 (first ~80 ms): press Jump, commit moving forward to carry
      // the kick's forward impulse into the opponent.
      // Phase 1 (after ~150 ms): press JumpKick while airborne → triggers
      // the actual kick (bothAir=true). We keep holding forward the whole
      // sequence so the character launches toward the target.
      frame.moveZ = -1;
      if (this.jumpKickPhase === 0) {
        frame.buttons |= InputBtn.Jump;
        if (this.stateT > 0.08) this.jumpKickPhase = 1;
      } else {
        frame.buttons |= InputBtn.JumpKick;
      }
    } else if (this.state === 'GrabAndThrow') {
      // Walk in to grab range, then stand still and hold Grab. Once both
      // hands are stuck we hold position and wait for the throw timer.
      if (dist > GRAB_ATTEMPT_DIST) frame.moveZ = -1;
      frame.buttons |= InputBtn.Grab;
    } else if (this.state === 'ThrowKOd') {
      // Walk in to a tighter-than-ideal distance so the latch is reliable on
      // a ragdolled target, then hold Grab. Same throw timing as the
      // standing path.
      if (dist > KO_GRAB_APPROACH_DIST) frame.moveZ = -1;
      frame.buttons |= InputBtn.Grab;
    }

    // Edge-press one-shot for Punch (the throw release). Emit the bit on
    // exactly `punchPressTicks` upcoming ticks.
    if (this.punchPressTicks > 0) {
      frame.buttons |= InputBtn.Punch;
      this.punchPressTicks -= 1;
    }

    // Always face the opponent — Character.targetYaw only updates while
    // wishMag > 0.001, so Idle / Attack (no movement input) would otherwise
    // keep punching in the last direction. A sub-threshold forward nudge
    // (< 0.08 wishMag) rotates facing without producing any locomotion.
    if (Math.abs(frame.moveX) < 1e-3 && Math.abs(frame.moveZ) < 1e-3) {
      frame.moveZ = -0.01;
    }

    return frame;
  }

  /** Showboat: hop in place, slowly spin, throw the occasional shadow-punch.
   *  Held until the next produce() resolves the `Showboat` precondition false. */
  private produceShowboatFrame(frame: InputFrame, _me: CharacterSnapshot, dt: number): InputFrame {
    // Slow yaw spin without moving.
    const yaw = wrapAngle(this.lastAimYaw + SHOWBOAT_SPIN_RATE * dt);
    this.lastAimYaw = yaw;
    frame.aimYaw = yaw;
    frame.moveZ = -0.01; // keep targetYaw rotating with aim

    this.showboatJumpTimer -= dt;
    if (this.showboatJumpTimer <= 0) {
      frame.buttons |= InputBtn.Jump;
      this.showboatJumpTimer = SHOWBOAT_JUMP_PERIOD_SEC;
    }
    this.showboatPunchTimer -= dt;
    if (this.showboatPunchTimer <= 0) {
      frame.buttons |= InputBtn.Punch;
      this.showboatPunchTimer = SHOWBOAT_PUNCH_PERIOD_SEC;
    }
    return frame;
  }

  private collectLivingEnemies(me: CharacterSnapshot, all: CharacterSnapshot[]): CharacterSnapshot[] {
    const teamOf = this.opts.teamOf;
    const myTeam = teamOf ? teamOf(me.id) : null;
    const out: CharacterSnapshot[] = [];
    for (const c of all) {
      if (c.id === me.id) continue;
      if (teamOf && myTeam !== null && teamOf(c.id) === myTeam) continue;
      if (c.ringedOut || c.wasted) continue;
      out.push(c);
    }
    return out;
  }

  private pickTarget(me: CharacterSnapshot, enemies: CharacterSnapshot[]): CharacterSnapshot | null {
    if (enemies.length === 0) return null;
    // Build the set of enemies already claimed by an AI teammate so we can
    // prefer untaken ones. Ourselves' previous target is never marked taken
    // (we only see what the *other* AIs picked).
    const taken = new Set<number>();
    if (this.opts.getTeammateTargetIds) {
      for (const id of this.opts.getTeammateTargetIds(me.id)) taken.add(id);
    }
    let bestUntaken: CharacterSnapshot | null = null;
    let bestUntakenD2 = Infinity;
    let bestAny: CharacterSnapshot | null = null;
    let bestAnyD2 = Infinity;
    for (const c of enemies) {
      const ddx = c.pelvisX - me.pelvisX;
      const ddz = c.pelvisZ - me.pelvisZ;
      const d2 = ddx * ddx + ddz * ddz;
      if (d2 < bestAnyD2) { bestAnyD2 = d2; bestAny = c; }
      if (!taken.has(c.id) && d2 < bestUntakenD2) { bestUntakenD2 = d2; bestUntaken = c; }
    }
    return bestUntaken ?? bestAny;
  }

  /** True if any *teammate* (AI or human) is currently within combat range
   *  of `target`. Caller already established `target` is the only living
   *  enemy, so this is the cue to switch to Showboat. */
  private teammateEngaging(me: CharacterSnapshot, target: CharacterSnapshot, all: CharacterSnapshot[]): boolean {
    const teamOf = this.opts.teamOf;
    if (!teamOf) return false;
    const myTeam = teamOf(me.id);
    for (const c of all) {
      if (c.id === me.id) continue;
      if (teamOf(c.id) !== myTeam) continue;
      if (c.ringedOut || c.wasted) continue;
      const ddx = c.pelvisX - target.pelvisX;
      const ddz = c.pelvisZ - target.pelvisZ;
      if (ddx * ddx + ddz * ddz < SHOWBOAT_ENGAGED_RANGE * SHOWBOAT_ENGAGED_RANGE) return true;
    }
    return false;
  }

  /** Periodic LOS raycast against arena. On miss → losClear=true and dodge
   *  drains. On hit → losClear=false and we start a sidestep walk in the
   *  ±60° direction with the cleaner ray. Cost: ≤ 3 raycasts per second per
   *  NPC, gated to maps that need it. */
  private maybeUpdateLOS(me: CharacterSnapshot, opp: CharacterSnapshot, dt: number): void {
    this.losCheckTimer -= dt;
    if (this.losCheckTimer > 0) return;
    this.losCheckTimer = LOS_CHECK_INTERVAL_SEC;
    const cast = this.opts.raycastArena;
    if (!cast) return;
    const ox = me.pelvisX, oy = me.pelvisY + LOS_RAY_VERT_OFFSET, oz = me.pelvisZ;
    const tx = opp.pelvisX, ty = opp.pelvisY + LOS_RAY_VERT_OFFSET, tz = opp.pelvisZ;
    const dxw = tx - ox, dyw = ty - oy, dzw = tz - oz;
    const distFull = Math.hypot(dxw, dyw, dzw);
    if (distFull < 1.2) { this.losClear = true; return; }
    const inv = 1 / distFull;
    const dirX = dxw * inv, dirY = dyw * inv, dirZ = dzw * inv;
    const hitToi = cast({ x: ox, y: oy, z: oz }, { x: dirX, y: dirY, z: dirZ }, distFull - 0.4);
    if (hitToi == null) {
      this.losClear = true;
      return;
    }
    this.losClear = false;
    // Try sidesteps. Rotate the XZ direction by ±LOS_DODGE_ANGLE and pick
    // whichever side has a clearer short ray. These rays are projected
    // outward at the level of the body, not toward the target — we just
    // want a corridor to step into.
    const flatLen = Math.hypot(dxw, dzw) || 1;
    const fX = dxw / flatLen, fZ = dzw / flatLen;
    const cosA = Math.cos(LOS_DODGE_ANGLE), sinA = Math.sin(LOS_DODGE_ANGLE);
    const lX = fX * cosA - fZ * sinA, lZ = fX * sinA + fZ * cosA;   // +60°
    const rX = fX * cosA + fZ * sinA, rZ = -fX * sinA + fZ * cosA;  // -60°
    const lToi = cast({ x: ox, y: oy, z: oz }, { x: lX, y: 0, z: lZ }, LOS_DODGE_RAY_DIST) ?? LOS_DODGE_RAY_DIST;
    const rToi = cast({ x: ox, y: oy, z: oz }, { x: rX, y: 0, z: rZ }, LOS_DODGE_RAY_DIST) ?? LOS_DODGE_RAY_DIST;
    const pickLeft = lToi >= rToi;
    const dxFinal = pickLeft ? lX : rX;
    const dzFinal = pickLeft ? lZ : rZ;
    this.dodgeAimYaw = Math.atan2(dxFinal, dzFinal);
    this.dodgeActive = LOS_DODGE_HOLD_SEC;
  }

  /** Decide whether to pop into the Climb micro-state based on the latest
   *  observation. Two triggers:
   *    1. We're in water and the target isn't → climb out.
   *    2. Target is significantly above us and we're already close → climb up.
   *  Both behaviours run the same Climb input loop (Grab + pulsed Jump +
   *  forward), which is what the user described as "grab+jump+forward". */
  private maybeEnterClimb(me: CharacterSnapshot, opp: CharacterSnapshot, dist: number, dy: number): void {
    if (this.state === 'Climb' || this.state === 'Stunned') return;
    const inWaterMustExit = me.isSwimming && !opp.isSwimming;
    const targetAbove = dy > CLIMB_HEIGHT_DELTA && dist < CLIMB_HORIZONTAL_DIST;
    if (!inWaterMustExit && !targetAbove) return;
    this.state = 'Climb';
    this.stateT = 0;
    this.climbDryTicks = 0;
  }

  /** Enter Attack state and pick a quick action (punch / headbutt). Hold
   *  durations come from per-difficulty tuning so easy AI does long flurries
   *  with clear breathing room while hard stays snappy. */
  private beginAttack(): void {
    const tune = DIFFICULTY_TUNING[this.opts.difficulty];
    this.state = 'Attack';
    this.stateT = 0;
    const roll = this.rng.next();
    if (roll < ACTION_PUNCH) {
      this.heldBtns = InputBtn.Punch;
      // Add a touch of jitter so two NPCs in the same fight don't perfectly
      // sync their attack windows.
      this.heldRemaining = tune.punchBurstSec * (0.85 + this.rng.next() * 0.30);
    } else {
      void ACTION_HEADBUTT; // mix constant — roll ≥ PUNCH falls into headbutt
      this.heldBtns = InputBtn.JumpKick; // resolves to headbutt on the ground
      this.heldRemaining = tune.headbuttHoldSec;
    }
  }

  static defaults(selfId: number, difficulty: AIDifficulty): AIOptions {
    const map = {
      passive: { reactionTicks: 0, aimNoise: 0 },
      easy:    { reactionTicks: 8, aimNoise: 0.45 },
      normal:  { reactionTicks: 4, aimNoise: 0.18 },
      hard:    { reactionTicks: 1, aimNoise: 0.06 }
    } as const;
    return { selfId, difficulty, ...map[difficulty] };
  }
}

function wrapAngle(a: number): number {
  while (a > Math.PI) a -= 2 * Math.PI;
  while (a < -Math.PI) a += 2 * Math.PI;
  return a;
}
